import TrainTimetable from './train-timetable';

/**
 * NYC Subway CBTC-signaled lines (L, 7) report real-time trip IDs without
 * the trailing path/route-variant suffix that the static schedule carries
 * (e.g. realtime "009250_L..S" vs static "009250_L..S01R"). Stripping any
 * such suffix lets those trains still match their static timetable entry.
 * No-op for every other operator's ID format.
 * @param {string} trainId - Train ID to normalize
 * @returns {string} Normalized train ID
 */
function normalizeTrainId(trainId) {
    return trainId.replace(/(\.\.[NS])[0-9A-Z]*$/, '$1');
}

export default class {

    constructor(data, refs) {
        const me = this,
            lookup = new Map();

        me.array = [];
        me.trainLookup = new Map();
        me.trainLookupNormalized = new Map();
        me.railwayDirectionLookup = new Map();

        for (const item of data) {
            const timetable = new TrainTimetable(item, refs);

            me.add(timetable);
            lookup.set(timetable.id, timetable);
        }

        for (const item of data) {
            if (item.pt || item.nt) {
                const timetable = lookup.get(item.id);

                timetable.update(item, Object.assign({}, refs, {timetables: lookup}));
            }
        }
    }

    add(timetable) {
        const {array, trainLookup, trainLookupNormalized, railwayDirectionLookup} = this,
            trainId = timetable.t,
            normalizedTrainId = normalizeTrainId(trainId),
            timetablesByTrain = trainLookup.get(trainId),
            timetablesByNormalizedTrain = trainLookupNormalized.get(normalizedTrainId),
            railwayDirectionId = `${timetable.r.id}:${timetable.d.id}`,
            timetablesByDirection = railwayDirectionLookup.get(railwayDirectionId);

        array.push(timetable);

        if (Array.isArray(timetablesByTrain)) {
            timetablesByTrain.push(timetable);
        } else {
            trainLookup.set(trainId, timetablesByTrain ? [timetablesByTrain, timetable] : timetable);
        }

        if (Array.isArray(timetablesByNormalizedTrain)) {
            timetablesByNormalizedTrain.push(timetable);
        } else {
            trainLookupNormalized.set(normalizedTrainId, timetablesByNormalizedTrain ? [timetablesByNormalizedTrain, timetable] : timetable);
        }

        if (timetablesByDirection) {
            timetablesByDirection.push(timetable);
        } else {
            railwayDirectionLookup.set(railwayDirectionId, [timetable]);
        }
    }

    getByTrainId(trainId) {
        const timetables = this.trainLookup.get(trainId) || this.trainLookupNormalized.get(normalizeTrainId(trainId));

        return Array.isArray(timetables) ? timetables : timetables ? [timetables] : [];
    }

    getByDirectionId(railwayId, DirectionId) {
        return this.railwayDirectionLookup.get(`${railwayId}:${DirectionId}`) || [];
    }

    getAll() {
        return this.array;
    }

    getConnectingTrainIds(trainId) {
        const ids = new Set();

        for (const timetable of this.getByTrainId(trainId)) {
            for (const id of timetable.getConnectingTrainIds()) {
                ids.add(id);
            }
        }
        return Array.from(ids);
    }

    clear() {
        const me = this;

        me.array = [];
        me.trainLookup = new Map();
        me.trainLookupNormalized = new Map();
        me.railwayDirectionLookup = new Map();
    }

}
